import { Locales } from '@griply/types';
import { FrequencyUnit } from 'shared/types/habit-schedule';

const localeWordMap: Record<
  Locales,
  {
    time: string;
    times: string;
    per: string;
    day: string;
    week: string;
    month: string;
    year: string;
  }
> = {
  [Locales.NlNL]: {
    time: 'keer',
    times: 'keer',
    per: 'per',
    day: 'dag',
    week: 'week',
    month: 'maand',
    year: 'jaar',
  },
  [Locales.EsES]: {
    time: 'vez',
    times: 'veces',
    per: 'por',
    day: 'día',
    week: 'semana',
    month: 'mes',
    year: 'año',
  },
  [Locales.DeDE]: {
    time: 'mal',
    times: 'mal',
    per: 'pro',
    day: 'tag',
    week: 'woche',
    month: 'monat',
    year: 'jahr',
  },
  [Locales.EnUS]: {
    time: 'time',
    times: 'times',
    per: 'per',
    day: 'day',
    week: 'week',
    month: 'month',
    year: 'year',
  },
};

const localeRegexWordMap: Record<
  Locales,
  {
    times: string;
    per: string;
    day: string;
    week: string;
    month: string;
    year: string;
  }
> = {
  [Locales.NlNL]: {
    times: 'keer',
    per: '(?:per|in de|in het)',
    day: 'dag',
    week: 'week',
    month: 'maand',
    year: 'jaar',
  },
  [Locales.EsES]: {
    times: 'veces',
    per: '(?:por|a la|al)',
    day: 'd[íi]a',
    week: 'semana',
    month: 'mes',
    year: 'año',
  },
  [Locales.DeDE]: {
    times: 'mal',
    per: 'pro',
    day: 'tag',
    week: 'woche',
    month: 'monat',
    year: 'jahr',
  },
  [Locales.EnUS]: {
    times: 'times?',
    per: '(?:per|a)',
    day: 'day',
    week: 'week',
    month: 'month',
    year: 'year',
  },
};

// Generates the regex pattern for targets like "5 times per week".
export const getTargetPatternContent = (language: Locales) => {
  const keywords =
    localeRegexWordMap[language] ?? localeRegexWordMap[Locales.EnUS];
  return `(\\d+)\\s+${keywords.times}\\s+${keywords.per}\\s+(${keywords.week}|${keywords.month}|${keywords.year})`;
};

/**
 * Parses a target string (e.g., "2 times per day") into a structured object.
 */
export const parseTarget = (targetStr: string, language: Locales) => {
  if (!targetStr) {
    return null;
  }

  const targetPattern = getTargetPatternContent(language);
  const match = targetStr
    .toLowerCase()
    .match(new RegExp(`^${targetPattern}$`, 'i'));
  if (match) {
    const count = parseInt(match[1], 10);
    const periodStr = match[2];
    const wordMap: Record<Locales, Record<string, string>> = {
      [Locales.EnUS]: {
        day: 'day',
        week: 'week',
        month: 'month',
        year: 'year',
      },
      [Locales.NlNL]: {
        dag: 'day',
        week: 'week',
        maand: 'month',
        jaar: 'year',
      },
      [Locales.EsES]: {
        día: 'day',
        semana: 'week',
        mes: 'month',
        año: 'year',
      },
      [Locales.DeDE]: {
        tag: 'day',
        woche: 'week',
        monat: 'month',
        jahr: 'year',
      },
    };
    const periodEN = wordMap[language][periodStr] ?? periodStr;
    return {
      count,
      unit: periodEN as FrequencyUnit,
      targetString: `${count} ${language === Locales.NlNL ? 'keer' : 'times'}/${periodEN}`,
    };
  }
  return null;
};

export const parseTargetToString = (
  target: { count: number; unit: FrequencyUnit },
  language: Locales,
) => {
  const words = localeWordMap[language] ?? localeWordMap[Locales.EnUS];
  const frequency =
    target.unit === FrequencyUnit.Week
      ? words.week
      : target.unit === FrequencyUnit.Month
        ? words.month
        : words.year;
  return `${target.count} ${target.count === 1 ? words.time : words.times} ${words.per} ${frequency}`;
};
